<template>
  <div class="view explorer">
    <ul v-if="isExploreLayout" class="nav-list">
      <li class="nav-item" @click="sideBarOpen = true">
        <div class="burger-icon" />
      </li>
    </ul>
    <sidebar
      v-if="isExploreLayout"
      v-model="currentAppSlot"
      :open="sideBarOpen"
      @open="sideBarOpen = false"
    />
    <div :class="['sidebar-background', { 'is-open': sideBarOpen }]" @click="sideBarOpen = false" />
    <template v-if="!ready && !error">
      <div class="explorer__loader app-overlay">
        <div class="text">Loading</div>
      </div>
    </template>
    <template v-else>
      <main class="explorer-main">
        <template v-if="mainCubeId && mainIndId">
          <indicator v-if="mode === 'indicator'" />
          <plotter v-else />
        </template>
        <template v-else>
          <div class="explorer__disclaimer">
            <div class="explorer__tags-selector">
              <p class="explorer__tags-title">Start exploring by selecting an actor, database or theme</p>
              <div class="explorer__tags">
                <div
                  v-for="(group, groupName) in groups"
                  :key="groupName"
                  class="explorer__group"
                >
                  <div class="explorer__group__name">{{ groupName }}</div>
                  <div class="explorer__group__content">
                    <div
                      v-for="tag in group.sort((a, b) => a.label.en < b.label.en ? -1 : 1)"
                      :key="tag.id"
                      class="explorer__tag"
                      @click="openSidebarOnTag(tag.id)"
                    >
                      <div class="icon" :style="{ backgroundImage: 'url(' + tag.imageURL + ')' }" />
                      <label>{{ tag.label[lng] }}</label>
                    </div>
                  </div>
                </div>
              </div>
              <div class="explorer-logos" v-if="version == 3">
                <img
                  src="@/assets/images/DIT_logo.png"
                  alt="logo DIT"
                  loading="lazy"
                  class="logo"
                >
              </div>
            </div>
          </div>
        </template>
      </main>
    </template>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar/Sidebar.vue'
import Indicator from '@/components/Indicator/Indicator.vue'
import Plotter from '@/components/Plotter/Plotter.vue'

export default {
  components: { Sidebar, Indicator, Plotter },
  data() {
    return {
      preloaded: false,
      sideBarOpen: false,
      appSlot: 'main',
    }
  },
  computed: {
    ready() {
      return (
        this.preloaded &&
        (this.$store.state.query.mainCubeId !== null
          ? this.$store.state.cubeMeta.main !== null
          : true)
      )
    },
    error() {
      return Boolean(this.$store.state.xhrError.data || this.$store.state.xhrError.api)
    },
    mainCubeMeta() {
      return this.$store.state.cubeMeta.main
    },
    mainIndId() {
      return this.$store.state.query.mainIndId
    },
    mainCubeId() {
      return this.$store.state.query.mainCubeId
    },
    mainCube() {
      return this.$store.getters.availableCubes.find(cube => cube.metaId === this.mainCubeId)
    },
    mode() {
      return this.$route.params.mode
    },
    currentAppSlot: {
      get() {
        return this.mode === 'indicator' ? 'main' : this.appSlot
      },
      set(to) {
        if (to !== this.currentAppSlot) this.sideBarOpen = true
        this.appSlot = to
      },
    },
    version() {
      return this.$route.params.version
    },
    groups () {
      return this.firstLevelTags.reduce((acum, item) => {
        let group = item.group !== void 0 && item.group[this.lng] || 'default'
        acum[group] = acum[group] || []
        acum[group].push(item)
        return acum
      }, {})
    },
    firstLevelTags() {
      return this.$store.getters.tagType('firstLevel').map(tag => {
        return {
          label: tag.tagName,
          id: tag.id,
          imageURL: tag.imageURL ? `${process.env.VUE_APP_CMS_URL}/${tag.imageURL}` : '',
          group: tag.parent?.tagName,
        }
      })
    },
  },
  beforeMount() {
    Promise.all([
      this.$store.dispatch('getDisclaimer', this.version),
      this.$store.dispatch('getCountriesGroups'),
      this.$store.dispatch('getExportSources'),
    ]).then(() => (this.preloaded = true))
  },
  mounted() {
    this.$root.$on('sidebar:open', slot => {
      if (this.currentAppSlot == slot) {
        this.sideBarOpen = true
      } else {
        this.currentAppSlot = slot
      }
    })
    document.getElementById("app").scrollTo(0, 0)
    if (this.$store.state.tags.preselection) {
      this.openSidebarOnTag(this.$store.state.tags.preselection)
      this.$store.commit('preselection', null)
    }
  },
  methods: {
    openSidebarOnTag(tagId) {
      this.sideBarOpen = true
      this.$root.$emit('sidebar:tag', tagId)
    },
  },
}
</script>

<style lang="stylus">
.explorer
  display: flex
  min-height: 100%
  position: relative

.nav-list
  align-items: center
  border-right: 1px solid $gray
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.05)
  display: flex
  flex: 0 0 vw(70px)
  flex-direction: column
  padding-bottom: vw(15px)
  padding-top: vw(15px)
  width: vw(70px)

  .mobile &
    background-color: transparent
    border-right: unset
    box-shadow: unset
    flex-direction: row
    left: 0
    padding: 0
    position: absolute
    width: 100%
    z-index: 1

.nav-item
  align-items: center
  border-radius: vw(15px)
  cursor: pointer
  display: flex
  height: vw(50px)
  justify-content: center
  width: vw(50px)

  .mobile &
    height: 45px
    width: auto
    margin-left: vw(20px)

  &:hover
    background-color: $light-gray

  .burger-icon
    m-icon("burger", 20)

    .mobile &
      +m-breakpoint(landscape)
        width: 22px
        height: 22px

.sidebar-background
  background-color: #fff
  display: none
  height: 100%
  left: 0
  opacity: 0.75
  position: absolute
  top: 0
  width: 100%
  z-index: 11

  &.is-open
    display: block

  // .version-1 &
  //   top: 102px

  //   @media screen and (max-width: 1024px)
  //     top: 64px

.explorer-main
  display: flex
  flex: 1 1 0%

  .mobile &
    width: 100%

.explorer-no-selection
  m-font-size(24, 30)
  align-items: center
  color: $dark-gray
  display: flex
  height: 100%
  justify-content: center
  width: 100%

.explorer__disclaimer
  height: 100%
  width: 100%
  padding: 0px vw(30px)
  padding-top: vw(15px)
  display: flex
  flex-direction: column
  justify-content: center

  .mobile &
    justify-content: flex-start
    overflow: scroll
    padding: 0px

.explorer__disclaimer-message
  flex: 1
  display: flex
  align-items: flex-end

  .mobile &
    flex: .6
    margin: 0 10%

.explorer__disclaimer-text
.explorer__tags-title
  font-size: 1.5rem
  color: $black
  margin-bottom: vw(20px)

  .mobile &
    text-align: center
    font-size: 1rem
    margin-bottom: vw(15px)
    margin-top: vw(15px)

  > a
    color: $blue-2

.explorer__tags-selector
  flex: 1
  display: flex
  flex-direction: column

  .mobile &
    margin: 0 12%

.explorer__tags
  display: flex
  justify-content: flex-start
  align-items: center
  flex-wrap: wrap

.explorer__tags-title
  font-size: 1.3rem

  .mobile &
    font-size: 1rem

.explorer__group
  margin-bottom: vw(20px)
  width 100%

.explorer__group__name
  m-font-size(16, 16)
  padding: vw(6px) vw(0px)
  color: #828282
  font-family: "Caecilia LT Pro", Georgia, sans

  .mobile &
    +m-breakpoint(landscape)
      font-size: 0.9rem
      line-height: 1
  
.explorer__group__content
  display: flex
  flex-wrap: wrap
  width: 100%

.explorer__tag
  padding: .3em .55em
  border-radius: 8px
  display: flex
  flex-direction: column
  cursor: pointer
  margin: .25em
  align-items: center

  .mobile &
    flex 1 0 20%
    padding 10px 0px

  &:hover
    background-color: rgba($havelock-blue, 0.05)

  > .icon
    width: 40px
    height: 40px
    background-repeat: no-repeat
    background-size: contain
    background-position: center

    .mobile &
      width: 30px
      height: 30px

  > label
    m-font-size(13,16)
    cursor: pointer
    text-align: center
    margin-top 8px
    color: #333333
    width 100px

    .mobile &
      line-height: 20px
      margin: 0 .2em 0 .25em
      width 80px

      +m-breakpoint(landscape)
        font-size: 0.74rem

.explorer__loader
  background-image: url("../assets/images/oecd-loader.gif")
  background-size: 100px !important

  .mobile &
    background-size: 100px !important

  .text
    padding-top: 180px

    .mobile &
      padding-top: 140px
.explorer-logos
  margin-top auto
  margin-bottom: 40px
  img.logo
    margin: 0 auto
    height: auto
    width: 150px
</style>
